import React, { useMemo } from 'react';
import type { TableProps } from 'antd';
import { AppTable } from '@/components/atoms';

interface ScheduleTableProps {
  amTimeStart?: string;
  amTimeEnd?: string;
  pmTimeStart?: string;
  pmTimeEnd?: string;
  schedule: {
    am: {
      monday: boolean;
      tuesday: boolean;
      wednesday: boolean;
      thursday: boolean;
      friday: boolean;
      saturday: boolean;
      sunday: boolean;
    };
    pm: {
      monday: boolean;
      tuesday: boolean;
      wednesday: boolean;
      thursday: boolean;
      friday: boolean;
      saturday: boolean;
      sunday: boolean;
    };
  };
}

const DAYS = [
  { key: 'monday', label: '月' },
  { key: 'tuesday', label: '火' },
  { key: 'wednesday', label: '水' },
  { key: 'thursday', label: '木' },
  { key: 'friday', label: '金' },
  { key: 'saturday', label: '土' },
  { key: 'sunday', label: '日' },
] as const;

interface ScheduleRow {
  key: string;
  typeLabel: string;
  timeRange: string;
  monday: boolean;
  tuesday: boolean;
  wednesday: boolean;
  thursday: boolean;
  friday: boolean;
  saturday: boolean;
  sunday: boolean;
}

/** Display as HH:MM (strip seconds when input is HH:MM:SS). */
function formatTimeHHMM(value?: string | null): string {
  if (!value) return '00:00';
  const v = value.trim();
  if (!v || v === '---' || v === '—') return '00:00';
  if (/^\d{2}:\d{2}:\d{2}$/.test(v)) return v.slice(0, 5);
  if (/^\d{2}:\d{2}$/.test(v)) return v;
  return v;
}

export const ScheduleTable: React.FC<ScheduleTableProps> = ({
  amTimeStart,
  amTimeEnd,
  pmTimeStart,
  pmTimeEnd,
  schedule,
}) => {
  const dataSource: ScheduleRow[] = useMemo(() => [
    {
      key: 'am',
      typeLabel: '午前',
      timeRange: `${formatTimeHHMM(amTimeStart)}～${formatTimeHHMM(amTimeEnd)}`,
      ...schedule.am,
    },
    {
      key: 'pm',
      typeLabel: '午後',
      timeRange: `${formatTimeHHMM(pmTimeStart)}～${formatTimeHHMM(pmTimeEnd)}`,
      ...schedule.pm,
    },
  ], [amTimeStart, amTimeEnd, pmTimeStart, pmTimeEnd, schedule]);

  const renderCell = (isActive: boolean) => (
    <div className="flex justify-center">
      {isActive ? (
        <span className="text-xl font-bold text-blue-600">〇</span>
      ) : (
        <span className="font-bold text-gray-300">ー</span>
      )}
    </div>
  );

  const columns: TableProps<ScheduleRow>['columns'] = useMemo(() => [
    {
      title: '時間',
      key: 'time',
      width: 240,
      render: (_, record) => (
        <div className="space-y-0.5">
          <div className="text-gray-400 font-normal uppercase tracking-wider text-[11px]">{record.typeLabel}</div>
          <div className="text-sm text-gray-700 whitespace-nowrap">{record.timeRange}</div>
        </div>
      ),
    },
    ...DAYS.map((day) => ({
      title: day.label,
      key: day.key,
      align: 'center' as const,
      render: (_: any, record: ScheduleRow) => renderCell(record[day.key as keyof ScheduleRow] as boolean),
    })),
  ], []);

  return (
    <div className="overflow-hidden bg-white border border-gray-200 rounded-lg shadow-sm schedule-table-container">
      <AppTable
        columns={columns}
        dataSource={dataSource}
        pagination={false}
        bordered={true}
      />
    </div>
  );
};
